import Vue from "vue";
import axios from "axios";

const state = {
	objects: [],
	listingType: "",
	sortOption: "",
	multiSelectFilters: [],
	rangeFilters: [],
	filtersApplied: true,
	defaultImageUrl: "",
	loading: false
};

axios.interceptors.request.use(function(config) {
	state.loading = true;
	return config;
});
axios.interceptors.response.use(
	function(response) {
		state.loading = false;
		return response;
	},
	function(error) {
		state.loading = false;
		console.log(error);
	}
);

const getters = {
	objects: state => {
		return state.objects;
	},
	listingType: state => {
		return state.listingType;
	},
	loading: state => {
		return state.loading;
	},
	filtersApplied: state => {
		return state.filtersApplied;
	},
	sortOption: state => {
		return state.sortOption;
	},
	multiSelectFilters: state => {
		return state.multiSelectFilters;
	},
	rangeFilters: state => {
		return state.rangeFilters;
	},
	currentObjects: (state, getters) => {
		let objects = getters.objects;
		let type = getters.listingType;
		if (state.filtersApplied) {
			// MULTISELECT FILTERS
			state.multiSelectFilters.map(filter => {
				let chosenOptions = filter.items
					.filter(i => i.checked)
					.map(option => option.display);
				if (filter.name === "districts") {
					objects = objects.filter(object =>
						chosenOptions.includes(object.district)
					);
				} else if (filter.name === "status") {
					objects = objects.filter(object =>
						chosenOptions.includes(object.status)
					);
				} else {
					objects = objects.filter(object => {
						let fact =
							object.facts &&
							object.facts.find(
								fact => fact.name === filter.name
							);
						if (fact && chosenOptions.includes(fact.displayValue)) {
							return object;
						}
					});
				}
			});
			// RANGE FILTERS
			state.rangeFilters.map(filter => {
				objects = objects.filter(object => {
					let fact = object.facts.find(
						fact => fact.name === filter.name
					);
					if (
						fact &&
						fact.value >= filter.selectedMin &&
						fact.value <= filter.selectedMax
					) {
						return true;
					}
				});
			});
		}
		if (!objects || objects.length === 0) return [];
		let option = state.sortOption;
		let currentObjects = [];

		// Sort on title when listing districts
		if (type === "district") {
			currentObjects = objects.sort((a, b) =>
				a.title > b.title ? 1 : -1
			);
		} else if (type === "development") {
			// "Sorting" from server slide
			currentObjects = objects;
		} else {
			switch (option) {
				case "publishedDate":
					currentObjects = objects.sort((a, b) =>
						a.publishedDate < b.publishedDate ? 1 : -1
					);
					break;
				case "rentalStart":
					currentObjects = objects.sort((a, b) =>
						a.rentalStart > b.rentalStart ? 1 : -1
					);
					break;
				case "address":
					currentObjects = objects.sort((a, b) =>
						a.address > b.address ? 1 : -1
					);
					break;
				case "district":
					currentObjects = objects.sort((a, b) =>
						a.district > b.district ? 1 : -1
					);
					break;
				default:
					// Default sort by publishedDate
					currentObjects = objects.sort((a, b) =>
						a.publishedDate < b.publishedDate ? 1 : -1
					);
			}
		}
		return currentObjects.sort(a => (a.isHighlighted ? -1 : 1));
	}
};

const actions = {
	fetchObjects({ dispatch }, options) {
		return new Promise(resolve => {
			const url = `${options.apiUrl}/api/ObjectList/${
				options.objectType === "development"
					? "FindDevelopmentPages"
					: "GetObjectList"
			}?type=${options.objectType}&t=${new Date().getTime()}`;

			axios
				.get(url, {
					crossDomain: true
				})
				.then(response => {
					if (response && response.data) {
						let data = response.data.data;
						dispatch("setObjects", {
							objects: data.objectItems,
							objectType: options.objectType
						});
						dispatch("setListingType", options.objectType);
						dispatch("setupFilters", data.objectFilters);
						this.state.loading = false;
						resolve();
					}
				});
		});
	},
	setListingType({ commit }, objectType) {
		commit("setListingType", objectType);
	},
	setObjects({ commit }, { objects, objectType }) {
		objects = objects.map(object => {
			object.objectType = objectType;
			if (!object.imageUrl) {
				object.imageUrl = state.defaultImageUrl;
			}
			return object;
		});
		commit("setObjects", objects);
	},
	setDefaultImageUrl({ commit }, defaultImageUrl) {
		commit("setDefaultImageUrl", defaultImageUrl);
	},
	setSortOption({ commit }, { option }) {
		commit("setSortOption", option);
	},
	setMultiSelectFilter({ commit }, filter) {
		commit("setMultiSelectFilter", filter);
	},
	setRangeFilter({ commit }, filter) {
		commit("setRangeFilter", filter);
	},
	resetAllFilters({ commit }) {
		commit("resetAllFilters");
	},
	setupFilters({ commit }, objectFilters) {
		let allMultiSelectRawOptions = objectFilters.filter(
			filter => filter.items
		);
		if (allMultiSelectRawOptions) {
			allMultiSelectRawOptions.map(rawOptions => {
				if (rawOptions.isEnabled && rawOptions.items) {
					let multiSelectFilter = {};
					multiSelectFilter.displayName = rawOptions.displayName;
					multiSelectFilter.name = rawOptions.name;
					multiSelectFilter.items = rawOptions.items.map(item => ({
						display: item,
						checked:
							!rawOptions.notSelected ||
							!rawOptions.notSelected.length ||
							!rawOptions.notSelected.includes(item)
					}));
					multiSelectFilter.items.unshift({
						toggleAll: true,
						display: "Alla" /* Specifik sträng från BE? */,
						checked: multiSelectFilter.items.every(
							i => i.checked == true
						)
					});
					commit("addMultiSelectFilter", multiSelectFilter);
				}
			});
		}
		let allRangeOptions = objectFilters.filter(filter => filter.minValue);
		if (allRangeOptions) {
			allRangeOptions.map(range => {
				if (range.isEnabled) {
					range.selectedMin = range.minValue;
					range.selectedMax = range.maxValue;
					commit("addRangeFilter", range);
				}
			});
		}
	}
};

const mutations = {
	setObjects(state, objects) {
		Vue.set(state, "objects", objects);
	},
	setListingType(state, listingType) {
		Vue.set(state, "listingType", listingType);
	},
	setDefaultImageUrl(state, defaultImageUrl) {
		Vue.set(state, "defaultImageUrl", defaultImageUrl);
	},
	addMultiSelectFilter(state, filter) {
		state.multiSelectFilters.push(filter);
	},
	addRangeFilter(state, filter) {
		state.rangeFilters.push(filter);
	},
	setMultiSelectFilter(state, filter) {
		let filterToUpdate = state.multiSelectFilters.find(
			msf => msf.name == filter.id
		);
		if (filterToUpdate) {
			Vue.set(filterToUpdate, "items", filter.options);
			Vue.set(state, "filtersApplied", true);
		}
	},
	setRangeFilter(state, filter) {
		let filterToUpdate = state.rangeFilters.find(
			rf => rf.name === filter.name
		);
		if (filterToUpdate) {
			Vue.set(filterToUpdate, "selectedMin", filter.selectedMin);
			Vue.set(filterToUpdate, "selectedMax", filter.selectedMax);
			Vue.set(state, "filtersApplied", true);
		}
	},
	setSortOption(state, option) {
		Vue.set(state, "sortOption", option);
	},
	setPriceRange(state, range) {
		Vue.set(state, "priceRange", range);
	},
	setRoomsRange(state, range) {
		Vue.set(state, "roomsRange", range);
	},
	setAreaRange(state, range) {
		Vue.set(state, "areaRange", range);
	},
	resetAllFilters(state) {
		state.multiSelectFilters.map(filter =>
			filter.items.map(item => {
				Vue.set(item, "checked", true);
			})
		);
		state.rangeFilters.map(filter => {
			filter.selectedMin = filter.minValue;
			filter.selectedMax = filter.maxValue;
		});
		Vue.set(state, "filtersApplied", false);
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
